import dayjs from 'dayjs';
import { MRT_ColumnDef } from 'mantine-react-table';
import { CSSProperties, ReactNode } from 'react';
import { NotePopoverCell } from '@/components/Hub/NotePopoverCell';
import { ORBIT_DISABLED, ORBIT_SETTLED } from '@/theme/colors';
import { LedgerCell as Cell, LedgerEmptyCell as Empty } from './LedgerCell';
import { ConnectCandidate } from './ledgerConnect';
import { LedgerPatientCell } from './LedgerPatientCell';
import {
  formatLedgerDate,
  formatLedgerMoney,
  ledgerMethod,
  LedgerRow,
  recognitionReasonLabel,
  sourceLabel,
} from './ledgerRows';

/** The prototype's one-line ledger row, column widths from its grid. */

const MONEY_STYLE: CSSProperties = { fontVariantNumeric: 'tabular-nums', textAlign: 'right' };
const RIGHT = { align: 'right' as const };

function money(
  id: string,
  header: string,
  size: number,
  value: (row: LedgerRow) => number | null,
  render: (amount: number) => ReactNode
): MRT_ColumnDef<LedgerRow> {
  return {
    id,
    header,
    size,
    enableSorting: false,
    accessorFn: (row) => value(row) ?? 0,
    mantineTableHeadCellProps: RIGHT,
    mantineTableBodyCellProps: RIGHT,
    Cell: ({ row }) => {
      const amount = row.original ? value(row.original) : null;
      return amount === null ? <Empty /> : <>{render(amount)}</>;
    },
  };
}

/** Finance's two edits on a row (BAR-3032). */
export interface LedgerRowActions {
  onSaveNote: (row: LedgerRow, note: string | null) => Promise<void>;
  onConnect: (row: LedgerRow, candidate: ConnectCandidate) => void;
  onDisconnect: (row: LedgerRow) => void;
}

export function ledgerColumns(actions: LedgerRowActions): MRT_ColumnDef<LedgerRow>[] {
  return [
    {
      id: 'date',
      header: 'Date',
      size: 124,
      sortingFn: 'datetime',
      accessorFn: (row) => (row?.occurredAt ? new Date(row.occurredAt) : null),
      Cell: ({ row }) => <Cell>{row.original?.occurredAt ? formatLedgerDate(row.original.occurredAt) : ''}</Cell>,
    },
    {
      id: 'patient',
      header: 'Patient',
      size: 140,
      grow: true,
      enableSorting: false,
      accessorFn: (row) => row?.patientDisplayName ?? '',
      Cell: ({ row }) =>
        row.original?.id ? (
          <LedgerPatientCell row={row.original} onConnect={actions.onConnect} onDisconnect={actions.onDisconnect} />
        ) : null,
    },
    {
      id: 'type',
      header: 'Type',
      size: 172,
      enableSorting: false,
      accessorFn: (row) => row?.type ?? '',
      Cell: ({ row }) => <Cell>{row.original?.type}</Cell>,
    },
    money(
      'amount',
      'Amount',
      92,
      (row) => row.amount,
      (amount) => <Cell style={MONEY_STYLE}>{formatLedgerMoney(amount)}</Cell>
    ),
    money(
      'fee',
      'Merchant fee',
      84,
      (row) => row.fee,
      (fee) => (
        <Cell style={MONEY_STYLE} color={fee > 0 ? ORBIT_SETTLED : undefined}>
          {formatLedgerMoney(fee, { sign: true })}
        </Cell>
      )
    ),
    money(
      'net',
      'Net',
      92,
      (row) => row.net,
      (net) => (
        <Cell style={MONEY_STYLE} weight={600}>
          {formatLedgerMoney(net)}
        </Cell>
      )
    ),
    {
      id: 'recognition',
      header: 'Recognition date',
      size: 100,
      enableSorting: false,
      // The date with its reason on hover; Deferred until there is one, and — with no patient.
      Cell: ({ row }) => {
        const item = row.original;
        if (!item?.id) return null;
        const reason = recognitionReasonLabel(item);
        return item.recognizedAt ? (
          <Cell title={reason}>{dayjs(item.recognizedAt).format('MMM D, YYYY')}</Cell>
        ) : (
          <Cell title={reason} color={ORBIT_DISABLED}>
            {item.medplumPatientId ? 'Deferred' : '—'}
          </Cell>
        );
      },
    },
    {
      id: 'transaction',
      header: 'Transaction ID',
      size: 100,
      enableSorting: false,
      accessorFn: (row) => row?.transactionId ?? '',
      Cell: ({ row }) =>
        row.original?.transactionId ? (
          <Cell title={row.original.transactionId}>{row.original.transactionId}</Cell>
        ) : (
          <Empty />
        ),
    },
    {
      id: 'source',
      header: 'Source',
      size: 80,
      enableSorting: false,
      accessorFn: (row) => sourceLabel(row?.source ?? null),
      // The prototype puts the method on hover: `Check · Mercury`.
      Cell: ({ row }) => (
        <Cell title={row.original ? ledgerMethod(row.original) || undefined : undefined}>
          {sourceLabel(row.original?.source ?? null)}
        </Cell>
      ),
    },
    {
      id: 'note',
      header: '',
      size: 28,
      enableSorting: false,
      Cell: ({ row }) => {
        const original = row.original;
        return original?.id ? (
          <NotePopoverCell note={original.note} onSave={(note) => actions.onSaveNote(original, note)} />
        ) : null;
      },
    },
  ];
}
